// Client-side encryption utilities using the Web Crypto API (AES-GCM 256).
// The key NEVER leaves the browser; it lives only in the URL fragment (after #).

const enc = new TextEncoder();
const dec = new TextDecoder();

// --- base64url helpers (URL-safe, no padding) ---
export function bytesToBase64Url(bytes) {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function base64UrlToBytes(b64) {
  const pad = b64.length % 4 === 0 ? "" : "=".repeat(4 - (b64.length % 4));
  const std = b64.replace(/-/g, "+").replace(/_/g, "/") + pad;
  const bin = atob(std);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// --- key generation / derivation ---
export async function generateRandomKey() {
  return crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, [
    "encrypt",
    "decrypt",
  ]);
}

export async function exportKeyToBase64(key) {
  const raw = await crypto.subtle.exportKey("raw", key);
  return bytesToBase64Url(new Uint8Array(raw));
}

export async function importKeyFromBase64(b64) {
  const raw = base64UrlToBytes(b64);
  return crypto.subtle.importKey("raw", raw, { name: "AES-GCM" }, false, [
    "encrypt",
    "decrypt",
  ]);
}

async function deriveKeyFromPassword(password, salt) {
  const passKey = await crypto.subtle.importKey(
    "raw",
    enc.encode(password),
    { name: "PBKDF2" },
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt, iterations: 250_000, hash: "SHA-256" },
    passKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

// --- encrypt / decrypt ---
// Returns { ciphertext, iv, keyB64 } where keyB64 should go into the URL fragment.
// If `password` is provided, the random AES key is itself encrypted by a
// password-derived key — the user MUST know the password to decrypt.
export async function encryptText(plaintext, password) {
  const aesKey = await generateRandomKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ctBuf = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    aesKey,
    enc.encode(plaintext),
  );

  let keyB64;
  let salt = null;
  if (password && password.length > 0) {
    salt = crypto.getRandomValues(new Uint8Array(16));
    const wrapKey = await deriveKeyFromPassword(password, salt);
    const wrapIv = crypto.getRandomValues(new Uint8Array(12));
    const rawAes = await crypto.subtle.exportKey("raw", aesKey);
    const wrapped = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: wrapIv },
      wrapKey,
      rawAes,
    );
    // pack: salt(16) | wrapIv(12) | wrappedKey(?)
    const packed = new Uint8Array(16 + 12 + wrapped.byteLength);
    packed.set(salt, 0);
    packed.set(wrapIv, 16);
    packed.set(new Uint8Array(wrapped), 28);
    keyB64 = "p:" + bytesToBase64Url(packed);
  } else {
    keyB64 = await exportKeyToBase64(aesKey);
  }

  return {
    ciphertext: bytesToBase64Url(new Uint8Array(ctBuf)),
    iv: bytesToBase64Url(iv),
    keyB64,
  };
}

export async function decryptText({ ciphertext, iv, keyB64, password }) {
  let aesKey;
  if (keyB64.startsWith("p:")) {
    if (!password) throw new Error("Password required");
    const packed = base64UrlToBytes(keyB64.slice(2));
    const salt = packed.slice(0, 16);
    const wrapIv = packed.slice(16, 28);
    const wrapped = packed.slice(28);
    const wrapKey = await deriveKeyFromPassword(password, salt);
    const rawAes = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: wrapIv },
      wrapKey,
      wrapped,
    );
    aesKey = await crypto.subtle.importKey(
      "raw",
      rawAes,
      { name: "AES-GCM" },
      false,
      ["encrypt", "decrypt"],
    );
  } else {
    aesKey = await importKeyFromBase64(keyB64);
  }

  const ptBuf = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: base64UrlToBytes(iv) },
    aesKey,
    base64UrlToBytes(ciphertext),
  );
  return dec.decode(ptBuf);
}

export function keyHasPassword(keyB64) {
  return typeof keyB64 === "string" && keyB64.startsWith("p:");
}
